/**
 * Export → patch → re-insert round-trip for the active slide.
 *
 * A few Format Tools ports need slide properties Office.js simply doesn't
 * expose — paragraph line spacing (`a:lnSpc`/`a:spcBef`/`a:spcAft`) and table
 * cell insets (`a:tcPr/@marL…`). The only way to set those from an add-in is
 * to export the slide as a single-slide .pptx, edit its XML, insert the edited
 * copy and drop the original — the same route SpeakerNotes and the brand fixer
 * already take (see `replaceSlideWithRewrittenCopy`).
 *
 * Cost of that route, which callers should surface to the user: the slide is
 * replaced, so the shape selection is cleared and the change lands as one
 * coarse undo step. Everything Office.js *can* do natively (shape margins,
 * word wrap, copy/apply formatting) stays on the native path instead.
 */
import { strFromU8, strToU8 } from "fflate";
import {
  replaceSlideWithRewrittenCopy,
  unzipPptxBase64,
} from "../../apply-brand/services/brandApplier";
import { hasPpt, ppt } from "../../../utils/formatUtils";
import { NS_P } from "../../../const";
import type { MatchTarget } from "./slideOoxml";

/** A single-slide export always writes its one slide to this part. */
const SLIDE_PART = "ppt/slides/slide1.xml";

const EXPORT_UNSUPPORTED =
  "This tool needs a newer version of PowerPoint (slide export isn't available here).";

/** One selected shape, as both the OOXML matcher and the callers need it. */
export interface SelectedShapeRef extends MatchTarget {
  /** Office.js `Shape.type` string — "Table", "Group", "GeometricShape", … */
  type: string;
}

export interface SlideRewriteContext {
  /** The selected shapes, in selection order, with geometry in points. */
  readonly selection: readonly SelectedShapeRef[];
  /** The exported slide's `<p:spTree>`. */
  readonly spTree: Element;
}

/**
 * Run `mutate` against the active slide's XML and write the result back.
 *
 * `mutate` returns the number of elements it changed; when that is 0 the slide
 * is left completely untouched (no re-insert, no lost selection) and the
 * `nothingChanged` message is thrown so the caller can explain why.
 */
export async function rewriteActiveSlide(
  mutate: (ctx: SlideRewriteContext) => number,
  nothingChanged: string,
): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");

  await ppt().run(async (context) => {
    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    const slides = context.presentation.getSelectedSlides();
    slides.load("items");
    await context.sync();

    if (sel.items.length === 0) throw new Error("Select at least one shape first.");
    const slide = slides.items[0];
    if (!slide) throw new Error("Couldn't detect the active slide. Please try again.");

    for (const s of sel.items) s.load("name,left,top,width,height,type");
    slide.load("id");
    await context.sync();

    const selection: SelectedShapeRef[] = sel.items.map((s) => ({
      name: s.name,
      left: s.left,
      top: s.top,
      width: s.width,
      height: s.height,
      type: String(s.type),
    }));

    // exportAsBase64 is PowerPointApi 1.5 — older hosts don't have it at all.
    const exportable = slide as PowerPoint.Slide & { exportAsBase64?: () => OfficeExtension.ClientResult<string> };
    if (typeof exportable.exportAsBase64 !== "function") throw new Error(EXPORT_UNSUPPORTED);
    const exported = exportable.exportAsBase64();
    await context.sync();

    const files = await unzipPptxBase64(exported.value);
    const part = files[SLIDE_PART];
    if (!part) throw new Error("Couldn't read this slide's content. Please save the file (Ctrl+S) and try again.");

    const doc = new DOMParser().parseFromString(strFromU8(part), "text/xml");
    if (doc.getElementsByTagName("parsererror").length > 0) {
      throw new Error("Couldn't read this slide's content. Please save the file (Ctrl+S) and try again.");
    }
    const spTree = doc.getElementsByTagNameNS(NS_P, "spTree")[0];
    if (!spTree) throw new Error("Couldn't read this slide's content. Please save the file (Ctrl+S) and try again.");

    const changed = mutate({ selection, spTree });
    if (changed === 0) throw new Error(nothingChanged);

    files[SLIDE_PART] = strToU8(new XMLSerializer().serializeToString(doc));
    await replaceSlideWithRewrittenCopy(
      context,
      files,
      slide.id,
      "KeepSourceFormatting" as PowerPoint.InsertSlideFormatting,
    );
  });
}
